/**
 * @file ui/src/pages/AdminLogin.tsx
 * @copyright 2026 Seth Zhai. BSL 1.1
 */

import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/auth";

export default function AdminLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: "#07130d",
        color: "#e8f6eb",
        fontFamily:
          "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: "min(420px, 100%)",
          border: "1px solid rgba(88, 209, 108, 0.22)",
          borderRadius: 12,
          padding: 28,
          background: "rgba(11, 30, 18, 0.86)",
          boxShadow: "0 24px 80px rgba(0, 0, 0, 0.34)",
        }}
      >
        <div style={{ color: "#58d16c", fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
          Spark KNE
        </div>
        <h1 style={{ margin: "0 0 6px", fontSize: 24 }}>Admin sign in</h1>
        <p style={{ margin: "0 0 24px", color: "#a9b9ad", fontSize: 14 }}>
          Authorized KNE review access.
        </p>

        <label style={{ display: "grid", gap: 8, marginBottom: 14, fontSize: 13 }}>
          Email
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
            style={inputStyle}
          />
        </label>

        <label style={{ display: "grid", gap: 8, marginBottom: 18, fontSize: 13 }}>
          Password
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
            style={inputStyle}
          />
        </label>

        {error && <p style={{ color: "#ff8a8a", fontSize: 13 }}>{error}</p>}

        <button
          type="submit"
          disabled={loading}
          style={{
            width: "100%",
            border: 0,
            borderRadius: 8,
            padding: "11px 14px",
            color: "#07130d",
            background: loading ? "#7ca984" : "#58d16c",
            fontWeight: 800,
            cursor: loading ? "wait" : "pointer",
          }}
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </main>
  );
}

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid rgba(232, 246, 235, 0.16)",
  borderRadius: 8,
  padding: "11px 12px",
  background: "rgba(255, 255, 255, 0.06)",
  color: "#e8f6eb",
  outline: "none",
} satisfies React.CSSProperties;
